import {
  Back,
  CloseSquare,
  Edit,
  Messages,
  Messages2,
  Send,
} from "iconsax-react";
import { useEffect, useState } from "react";
import {
  Link,
  useLoaderData,
  useNavigate,
  useRevalidator,
} from "react-router-dom";
import { toast } from "react-toastify";
import { getToken } from "../../../utils/token";
import instance from "../../../axios/instance";
import { RxMedicationName } from "../../../components/MedicationName";
import { ConfirmationModal } from "../../../components/modals/doctor/ConfirmationModal";
import MessageItem from "../../../components/modals/order/MessageItem";
import PatientMessageItem from "../../../components/modals/order/PatientMessageItem";

export const refillRequestItemLoader = async ({ params }) => {
  const token = getToken();
  const refillRequest = await instance.get(
    `/doctor/refill/single/${params.id}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (!refillRequest.data)
    return { refillRequest: null, messages: [], patientMessages: [] };

  const orderMessages = await instance.post(
    "/doctor/messages/facility",
    {
      orderId: refillRequest.data.order_id,
      pharmacyId: refillRequest.data.pharmacy_id,
    },
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  const patientMessages = await instance.post(
    "/doctor/messages/patient",
    {
      orderId: refillRequest.data.order_id,
      patientId: refillRequest.data.patient_id,
    },
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  const doctor = await instance.get("/doctor", {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return {
    messages: orderMessages.data,
    refillRequest: refillRequest.data,
    doctor: doctor.data,
    patientMessages: patientMessages.data,
  };
};

const RefillRequestItemPage = () => {
  const { messages, refillRequest, doctor, patientMessages } = useLoaderData();

  if (!refillRequest) {
    toast.error("This order does not exist");
    setTimeout(() => {
      history.back();
    }, 100);
    return;
  }

  const order = refillRequest.order;
  const pharmacy = refillRequest.pharmacy;
  const patient = refillRequest.patient;
  const rx = refillRequest.medicationRx;

  const navigate = useNavigate();
  const [openMessage, setOpenMessage] = useState(false);
  const revalidator = useRevalidator();
  const [loading, setLoading] = useState("");
  const [declineRequest, setDeclineRequest] = useState(null);
  const [approveRequest, setApproveRequest] = useState(null);
  const [openPatientMessage, setOpenPatientMessage] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState(
    messages.filter(
      (message) =>
        message.status === "unread" &&
        message.recipient_id === refillRequest.facility_id &&
        message.order_id === refillRequest.id
    )
  );
  const [unreadPatientMessages, setUnreadPatientMessages] = useState(
    patientMessages.filter(
      (message) =>
        message.status === "unread" &&
        message.recipient_id === refillRequest.facility_id &&
        message.order_id === refillRequest.id
    )
  );

  const handleStatusUpdate = async (status) => {
    const token = getToken();
    setLoading(status);
    try {
      const res = await instance.put(
        "/doctor/refill/request/edit",
        {
          id: refillRequest.id,
          status,
          orderId: order.id,
          patientId: patient.id,
          rxNumber: refillRequest.rx_number,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      toast.success(res.data.message);
      setDeclineRequest(null);
      setApproveRequest(null);
      revalidator.revalidate();
      if (status === "accepted") {
        navigate(
          `/dashboard/refill-requests/${refillRequest.id}/order/${order.id}`
        );
      }
      setLoading("");
    } catch (error) {
      toast.error("An unknown error occurred. Please try again");
      console.log(error);
      setLoading("");
    }
  };

  return (
    <>
      <div className="fixed top-0 right-0 h-[100vh] w-[450px] bg-white px-3 py-10 overflow-y-scroll z-30">
        <div className="flex items-center justify-between">
          <div className="text-[18px] font-medium select-none">
            Refill Request for RX {refillRequest.rx_number}
          </div>
          <button onClick={() => history.back()}>
            <CloseSquare variant="Bold" />
          </button>
        </div>
        <div className="border rounded-md px-3 py-3 mt-5 grid grid-cols-2 items-start gap-x-5 gap-y-5">
          <div>
            <h2 className="text-sm mb-1 capitalize text-[black] font-[500]">
              Status:
            </h2>
            <p className="text-xs capitalize">
              {refillRequest.status === "accepted"
                ? "approved"
                : refillRequest.status}
            </p>
          </div>
          <div>
            <h2 className="text-sm mb-1 capitalize text-[black] font-[500]">
              Pharmacy:
            </h2>
            <p className="text-xs capitalize">{pharmacy.pharmacy_name}</p>
          </div>
          <div>
            <h2 className="text-sm mb-1 capitalize text-[black] font-[500]">
              Patient:
            </h2>
            <Link
              to={`/dashboard/patients/${patient.id}`}
              className="inline-block underline text-primary text-xs capitalize"
            >
              {patient.lastname + " " + patient.firstname}
            </Link>
          </div>
          <div>
            <h2 className="mb-1 text-sm">
              Order ID{refillRequest.new_order_id ? "s" : ""}:
            </h2>
            {refillRequest.new_order_id ? (
              <>
                <span className="text-xs">
                  Old:{" "}
                  <Link
                    to={`/dashboard/orders/${order.id}`}
                    className="inline-block underline text-primary text-xs capitalize"
                  >
                    {order.id}
                  </Link>
                </span>
                <br />
                <span className="text-xs">
                  New:{" "}
                  <Link
                    to={`/dashboard/orders/${refillRequest.new_order_id}`}
                    className="inline-block underline text-primary text-xs capitalize"
                  >
                    {refillRequest.new_order_id}
                  </Link>
                </span>
              </>
            ) : (
              <Link
                to={`/dashboard/orders/${order.id}`}
                className="inline-block underline text-primary text-xs capitalize"
              >
                {order.id}
              </Link>
            )}
          </div>
          <div>
            <h2 className="mb-1 text-sm">Medication Name:</h2>
            <span className="text-xs text-gray-700 capitalize">
              <RxMedicationName rx={rx} />
            </span>
          </div>
          <div>
            <h2 className="mb-1 text-sm">Product Type:</h2>
            <span className="text-xs text-gray-700 capitalize">
              {rx.product_type || "--"}
            </span>
          </div>
          <div>
            <h2 className="mb-1 text-sm">Strength:</h2>
            <span className="text-xs text-gray-700">{rx.strength || "--"}</span>
          </div>
          <div>
            <h2 className="mb-1 text-sm">Form:</h2>
            <span className="text-xs text-gray-700">{rx.form || "--"}</span>
          </div>
          <div>
            <h2 className="mb-1 text-sm">Size:</h2>
            <span className="text-xs text-gray-700">{rx.size || "--"}</span>
          </div>
          <div>
            <h2 className="mb-1 text-sm">mG/ML:</h2>
            <span className="text-xs text-gray-700">{rx.mg || "--"}</span>
          </div>
          <div>
            <h2 className="mb-1 text-sm">Device:</h2>
            <span className="text-xs text-gray-700">{rx.device || "--"}</span>
          </div>
          <div>
            <h2 className="mb-1 text-sm">Route of Admin:</h2>
            <span className="text-xs text-gray-700">
              {rx.route_of_admin || "--"}
            </span>
          </div>
          <div>
            <h2 className="mb-1 text-sm">Category:</h2>
            <span className="text-xs text-gray-700">{rx.category || "--"}</span>
          </div>
          <div>
            <h2 className="mb-1 text-sm">Package:</h2>
            <span className="text-xs text-gray-700">{rx.package || "--"}</span>
          </div>
          <div>
            <h2 className="mb-1 text-sm">Quantity Dispensed:</h2>
            <span className="text-xs text-gray-700 capitalize">
              {rx.quantity_dispensed || "--"}
            </span>
          </div>
          <div>
            <h2 className="mb-1 text-sm">Units:</h2>
            <span className="text-xs text-gray-700">{rx.units || "--"}</span>
          </div>
          <div>
            <h2 className="mb-1 text-sm">Packages Dispensed:</h2>
            <span className="text-xs text-gray-700">
              {rx.packages_dispensed || "--"}
            </span>
          </div>
          <div>
            <h2 className="mb-1 text-sm">Refill Schedule:</h2>
            <span className="text-xs text-gray-700">
              {rx.refill_schedule || "--"}
            </span>
          </div>
          <div>
            <h2 className="mb-1 text-sm">Refill Remaining:</h2>
            <span className="text-xs text-gray-700">
              {rx.refill_remaining || "--"}
            </span>
          </div>
          <div>
            <h2 className="mb-1 text-sm">RX Serial Number:</h2>
            <span className="text-xs text-gray-700">
              {rx.serial_number || "--"}
            </span>
          </div>
          <div>
            <h2 className="mb-1 text-sm">RX Serial Number State:</h2>
            <span className="text-xs text-gray-700">
              {rx.serial_number_state || "--"}
            </span>
          </div>
          <div>
            <h2 className="mb-1 text-sm">RX Effective Date:</h2>
            <span className="text-xs text-gray-700">
              {rx.effective_date
                ? new Date(rx.effective_date).toDateString()
                : "--"}
            </span>
          </div>
          <div>
            <h2 className="mb-1 text-sm">RX Expiration Date:</h2>
            <span className="text-xs text-gray-700">
              {rx.expiration_date
                ? new Date(rx.expiration_date).toDateString()
                : "--"}
            </span>
          </div>
          <div>
            <h2 className="mb-1 text-sm">Created on:</h2>
            <span className="text-xs text-gray-700">
              {rx.created_at ? new Date(rx.created_at).toDateString() : "--"}
            </span>
          </div>
          <div>
            <h2 className="mb-1 text-sm">Last Updated:</h2>
            <span className="text-xs text-gray-700 capitalize">
              {rx.updated_at ? new Date(rx.updated_at).toDateString() : "--"}
            </span>
          </div>
          <div>
            <h2 className="mb-1 text-sm">Dispensed At:</h2>
            <span className="text-xs text-gray-700 capitalize">
              {rx.dispensed === "true"
                ? new Date(rx.dispensed_at).toDateString()
                : "--"}
            </span>
          </div>
        </div>
        <div className="my-3 grid grid-cols-2 gap-x-2 items-center">
          <button
            className="flex items-center gap-x-1 w-fit max-w-fit justify-center px-4 py-2 rounded-md border border-primary text-sm relative"
            onClick={() => setOpenPatientMessage(true)}
          >
            <Messages size={20} />
            Message Patient
            {unreadPatientMessages.length >= 1 && (
              <span className="block absolute top-2 right-2 w-[5px] h-[5px] bg-[tomato] rounded-full"></span>
            )}
          </button>
          <button
            className="flex items-center gap-x-1 w-fit max-w-fit justify-center px-4 py-2 rounded-md border border-primary text-sm relative"
            onClick={() => setOpenMessage(true)}
          >
            <Messages size={20} />
            Message Pharmacy
            {unreadMessages.length >= 1 && (
              <span className="block absolute top-2 right-2 w-[5px] h-[5px] bg-[tomato] rounded-full"></span>
            )}
          </button>
        </div>

        {refillRequest.status === "pending" && (
          <div className="mt-6">
            <button
              className={`bg-primary text-white w-full h-10 hover:border-primary border-primary duration-300 cursor-pointer border text-sm flex justify-center items-center rounded-md disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:border-[rgb(238,238,238)] mb-2`}
              onClick={() => setApproveRequest(refillRequest)}
            >
              {loading === "accepted" ? "approving..." : "Approve Request"}
            </button>
            <button
              className={`bg-[tomato] text-white w-full h-10 hover:border-[tomato] border-[tomato] duration-300 cursor-pointer border text-sm flex justify-center items-center rounded-md disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:border-[#eee]`}
              onClick={() => setDeclineRequest(refillRequest)}
            >
              {loading === "declined" ? "processing..." : "Decline Request"}
            </button>
          </div>
        )}
        {/* <div
          className="fixed bottom-10 right-10 bg-primary h-[45px] w-[45px] rounded-full flex items-center justify-center cursor-pointer"
          onClick={() => setOpenMessage(true)}
        >
          <Messages2 size="28" color="white" />
          {unreadMessages.length > 0 && (
            <span className="flex items-center justify-center w-[17px] h-[17px] rounded-full bg-[tomato] absolute top-0 -right-[5px] text-[10px] text-white">
              {unreadMessages.length > 9 ? "9+" : unreadMessages.length}
            </span>
          )}
        </div> */}

        {declineRequest && (
          <>
            <ConfirmationModal
              onClose={() => setDeclineRequest(null)}
              cta={() => handleStatusUpdate("declined")}
              msg={"Are you sure you want to decline this request?"}
            />
            <div
              className="fixed z-20 inset-0 bg-[rgba(0,0,0,.2)]"
              onClick={() => setDeclineRequest(null)}
            />
          </>
        )}
        {approveRequest && (
          <>
            <ConfirmationModal
              onClose={() => setApproveRequest(null)}
              cta={() => handleStatusUpdate("accepted")}
              msg={"Are you sure you want this request approved?"}
            />
            <div
              className="fixed z-20 inset-0 bg-[rgba(0,0,0,.2)]"
              onClick={() => setApproveRequest(null)}
            />
          </>
        )}
        {openMessage && (
          <MessageItem
            order={refillRequest}
            onClose={() => setOpenMessage(false)}
            isOpen={openMessage}
            dbMessages={messages}
            doctor={doctor}
            setUnreadMessages={setUnreadMessages}
          />
        )}
        {openPatientMessage && (
          <PatientMessageItem
            order={refillRequest}
            onClose={() => setOpenPatientMessage(false)}
            isOpen={openPatientMessage}
            dbMessages={patientMessages}
            doctor={doctor}
            setUnreadMessages={setUnreadPatientMessages}
          />
        )}
      </div>
      <div
        className="fixed z-20 inset-0 bg-[rgba(0,0,0,.2)]"
        onClick={() => history.back()}
      />
    </>
  );
};

export default RefillRequestItemPage;